import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, List, ListItem, ListItemText, Typography,
} from '@mui/material';
import {
  Loyalty as LoyaltyIcon, People as PeopleIcon, Stars as StarsIcon, Redeem as RedeemIcon, ShoppingCart as CartIcon,
  PointOfSale as CashierIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoyaltyService from '../../services/loyalty.service';
import { useTranslation } from '../../hooks/useTranslation';
import { formatMoney, formatPoints, ledgerColor } from './loyaltyUtils';
import { initializeCurrency } from '../../utils/currencyUtils';

const Kpi = ({ icon, label, value, sub }) => (
  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
    <Card sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Box minWidth={0}>
            <Typography color="text.secondary" variant="body2">{label}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box flexShrink={0}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

/**
 * Small trend bars (last 6 months) — no chart lib in the project.
 */
const TrendBars = ({ trend, keyName, color, label }) => {
  const max = Math.max(1, ...trend.map((m) => Number(m[keyName]) || 0));
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box display="flex" alignItems="flex-end" gap={1} height={80} mt={0.5}>
        {trend.map((m) => {
          const v = Number(m[keyName]) || 0;
          return (
            <Box key={m.month} flex={1} textAlign="center" title={`${m.month}: ${formatPoints(v)}`}>
              <Box sx={{ height: `${Math.max(4, (v / max) * 64)}px`, bgcolor: color, borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">{m.month.slice(5)}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Loyalty insights block for the merchant home dashboard. Rendered only when
 * the tenant has the loyalty program enabled (settings.loyalty.enabled).
 */
const LoyaltyDashboardSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for the tenant currency so amounts don't flash as "$".
    Promise.all([LoyaltyService.getDashboard(), initializeCurrency()])
      .then(([r]) => setData(r.data?.data || null))
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>;
  if (error) return <Alert severity="warning">{error}</Alert>;
  if (!data) return null;

  const delta = (cur, prev) => {
    if (!prev) return null;
    const pct = ((cur - prev) / prev) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}% ${t('loyalty.vsLastMonth')}`;
  };

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <LoyaltyIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">{t('loyalty.dashboardTitle')}</Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<CashierIcon />} onClick={() => navigate('/loyalty')}>{t('loyalty.cashierTitle')}</Button>
      </Box>

      <Grid container spacing={3}>
        <Kpi icon={<PeopleIcon color="primary" sx={{ fontSize: 40 }} />} label={t('loyalty.members')} value={formatPoints(data.members.total)} sub={`${t('loyalty.active30d')}: ${formatPoints(data.members.active_30d)} · ${t('loyalty.newThisMonth')}: ${data.members.new_this_month}`} />
        <Kpi icon={<CartIcon color="success" sx={{ fontSize: 40 }} />} label={t('loyalty.revenueThisMonth')} value={formatMoney(data.purchases.revenue_this_month)} sub={`${data.purchases.count_this_month} ${t('loyalty.purchases')} · ${t('loyalty.avgTicket')} ${formatMoney(data.purchases.avg_ticket_this_month)}${delta(data.purchases.revenue_this_month, data.purchases.revenue_last_month) ? ` · ${delta(data.purchases.revenue_this_month, data.purchases.revenue_last_month)}` : ''}`} />
        <Kpi icon={<StarsIcon color="warning" sx={{ fontSize: 40 }} />} label={t('loyalty.pointsIssuedThisMonth')} value={formatPoints(data.points.issued_this_month)} sub={`${t('loyalty.outstanding')}: ${formatPoints(data.points.outstanding)}`} />
        <Kpi icon={<RedeemIcon color="error" sx={{ fontSize: 40 }} />} label={t('loyalty.pointsRedeemedThisMonth')} value={formatPoints(data.points.redeemed_this_month)} sub={`${data.points.redemptions_this_month} ${t('loyalty.redemptions')} · ${t('loyalty.redemptionRate')} ${data.points.redemption_rate}%`} />

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>{t('loyalty.trend')}</Typography>
              <TrendBars trend={data.trend} keyName="revenue" color="success.main" label={t('loyalty.revenue')} />
              <Box mt={2}><TrendBars trend={data.trend} keyName="points_issued" color="warning.main" label={t('loyalty.pointsIssued')} /></Box>
              <Box mt={2}><TrendBars trend={data.trend} keyName="points_redeemed" color="error.main" label={t('loyalty.pointsRedeemed')} /></Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>{t('loyalty.topRewards30d')}</Typography>
              {data.top_rewards.length === 0 && <Typography variant="body2" color="text.secondary">{t('loyalty.noRedemptionsYet')}</Typography>}
              <List dense>
                {data.top_rewards.map((r, i) => (
                  <ListItem key={i} sx={{ px: 0, pr: 6 }} secondaryAction={<Chip size="small" label={`${r.redemptions_count}×`} />}>
                    <ListItemText primary={r.reward?.name || '—'} secondary={`${formatPoints(r.points_total)} ${t('loyalty.points')}`} />
                  </ListItem>
                ))}
              </List>
              <Typography variant="h6" fontWeight="bold" gutterBottom mt={2}>{t('loyalty.topMembers')}</Typography>
              <List dense>
                {data.top_members.map((m) => (
                  <ListItem key={m.uuid} sx={{ px: 0, pr: 7, cursor: 'pointer' }} onClick={() => navigate(`/loyalty/clients/${m.uuid}`)} secondaryAction={<b>{formatPoints(m.points_balance)}</b>}>
                    <ListItemText primary={m.name} secondary={`${m.visit_count} ${t('loyalty.visits').toLowerCase()} · ${formatMoney(m.total_spent)}`} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>{t('loyalty.recentActivity')}</Typography>
              {data.recent_activity.length === 0 && <Typography variant="body2" color="text.secondary">{t('loyalty.noActivityYet')}</Typography>}
              <List dense>
                {data.recent_activity.map((e) => (
                  <ListItem key={e.uuid} sx={{ px: 0, pr: 9 }} secondaryAction={
                    <Chip size="small" variant="outlined" color={ledgerColor(e.type)} label={`${e.points_change > 0 ? '+' : ''}${formatPoints(e.points_change)}`} />
                  }>
                    <ListItemText
                      primary={e.client?.name || '—'}
                      secondary={`${t(`loyalty.ledgerTypes.${e.type}`)}${e.description ? ` · ${e.description}` : ''}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoyaltyDashboardSection;
